import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import MistakeCard from '../components/player/MistakeCard';
import MistakeRetryModal from '../components/player/MistakeRetryModal';
import { SkeletonCard } from '../components/ui/Skeleton';
import Icon from '../components/ui/Icon';
import EmptyState from '../components/ui/EmptyState';
import ErrorState from '../components/ui/ErrorState';
import { useStudent } from '../components/student/StudentContext';
import './MistakeBank.css';

// Mistake Bank answers "what do I repeatedly need to fix" — distinct from Test Review (TestReview.js), which answers
// "what happened in the one attempt I just completed." Categories reuse mistakeIntelligenceService's own thresholds via
// the per-item is_recurring/is_recently_corrected flags getStudentMistakeBank returns — never re-derived here.
const MistakeBank = () => {
  const navigate = useNavigate();
  const { refreshPlayer } = useStudent();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(null);
  const [tab, setTab] = useState('active'); // active | corrected
  const [retryTarget, setRetryTarget] = useState(null);

  const fetchMistakes = async () => {
    setLoading(true); setFailed(null);
    try {
      const res = await API.get('/mistakes');
      setData(res.data);
    } catch (err) {
      setFailed(err);
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchMistakes(); }, []);

  const markResolvedLocally = (mistakeId) => {
    setData(d => ({
      ...d,
      mistakes: d.mistakes.map(m => m.id === mistakeId
        ? { ...m, is_resolved: true, is_recurring: false, is_recently_corrected: true }
        : m),
    }));
    // audit ST-10/ST-11: resolving a mistake completes here in-place (the retry modal stays on this
    // page), with no navigation to trigger StudentContext's pathname-keyed refresh — so the
    // "recover_mistake" mission needs its own explicit poke.
    refreshPlayer();
  };

  if (loading) {
    return <div className="z-page z-page--narrow" role="status" aria-label="Loading your mistakes"><SkeletonCard lines={2} /><SkeletonCard lines={3} /><SkeletonCard lines={3} /></div>;
  }
  if (failed) return <div className="z-page z-page--narrow"><ErrorState error={failed} onRetry={fetchMistakes} /></div>;

  const { mistakes = [], topicBreakdown = [] } = data || {};
  const active = mistakes.filter(m => !m.is_resolved);
  const recurring = active.filter(m => m.is_recurring);
  const needsRecovery = active.filter(m => !m.is_recurring);
  const corrected = mistakes.filter(m => m.is_resolved);
  const recentlyCorrected = corrected.filter(m => m.is_recently_corrected);
  const otherResolved = corrected.filter(m => !m.is_recently_corrected);

  const Group = ({ icon, title, items }) => (items.length > 0 ? (
    <div className="mb-group">
      <h3 className="mb-group-title"><Icon name={icon} size={16} /> {title}</h3>
      {items.map(m => <MistakeCard key={m.id} mistake={m} onRetry={setRetryTarget} />)}
    </div>
  ) : null);

  return (
    <div className="z-page z-page--narrow mb-body">
      <p className="mb-sub">Every mistake you understand is one less mistake you'll make again.</p>

      {mistakes.length === 0 ? (
        <EmptyState
          icon="mistake" title="Nothing to recover yet" body="Keep practising — questions you get wrong will show up here so you can win them back."
          action={{ label: 'Start a challenge', onClick: () => navigate('/student/practice') }}
        />
      ) : (
        <>
          <div className="mb-summary-row">
            <div className="mb-summary-tile mb-summary-tile--recover"><Icon name="mistake" size={20} /><span className="mb-summary-num">{active.length}</span><span className="mb-summary-label">Needs recovery</span></div>
            <div className="mb-summary-tile mb-summary-tile--recurring"><Icon name="refresh" size={20} /><span className="mb-summary-num">{recurring.length}</span><span className="mb-summary-label">Recurring</span></div>
            <div className="mb-summary-tile mb-summary-tile--done"><Icon name="success" size={20} /><span className="mb-summary-num">{corrected.length}</span><span className="mb-summary-label">Corrected</span></div>
          </div>

          <div className="z-segments" role="tablist" aria-label="Mistake status">
            <button type="button" role="tab" className="z-segment" aria-selected={tab === 'active'} onClick={() => setTab('active')}>Active ({active.length})</button>
            <button type="button" role="tab" className="z-segment" aria-selected={tab === 'corrected'} onClick={() => setTab('corrected')}>Corrected ({corrected.length})</button>
          </div>

          {tab === 'active' && (
            active.length === 0 ? (
              <EmptyState icon="success" art={false} tone="neutral" compact title="Nothing active right now" body="Nice work — you've recovered everything for now." />
            ) : (
              <div className="mb-list">
                <Group icon="refresh" title="Recurring" items={recurring} />
                <Group icon="mistake" title="Needs recovery" items={needsRecovery} />
              </div>
            )
          )}

          {tab === 'corrected' && (
            corrected.length === 0 ? (
              <EmptyState icon="mistake" art={false} tone="neutral" compact title="No corrections yet" body="Retry an active mistake to start recovering." />
            ) : (
              <div className="mb-list">
                <Group icon="success" title="Recently corrected" items={recentlyCorrected} />
                <Group icon="check" title="Mastered" items={otherResolved} />
              </div>
            )
          )}

          {topicBreakdown.length > 0 && (
            <section className="z-section" aria-labelledby="mb-weak">
              <h2 className="z-section-title" id="mb-weak">Weakest topics</h2>
              <div className="z-surface mb-weak-topics">
                {topicBreakdown.slice(0, 5).map((t, i) => {
                  const maxCount = topicBreakdown[0]?.count || 1;
                  const pct = Math.round((t.count / maxCount) * 100);
                  return (
                    <div key={i} className="mb-topic-row">
                      <span className="mb-topic-name">{t.topic || 'General'}</span>
                      <div className="mb-topic-track"><div className="mb-topic-fill" style={{ width: `${pct}%` }} /></div>
                      <span className="mb-topic-count">{t.count}</span>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </>
      )}

      {retryTarget && (
        <MistakeRetryModal mistake={retryTarget} onClose={() => setRetryTarget(null)} onResolved={markResolvedLocally} />
      )}
    </div>
  );
};

export default MistakeBank;
